/**
 * DraftPlan — the plan mode shell.
 *
 * Composes the toolbar, the league context line, PlanSummary, the STARTERS and
 * BENCH row groups, PlayerPanel and the footer. All state comes from the
 * usePlanState hook, passed in as `plan` so the parent keeps ownership of hook
 * order (CLAUDE.md: the main component is a thin JSX shell, hooks live in
 * src/hooks). This file renders; it does not own.
 *
 * THE LAYOUT IS A MATRIX, NOT A LIST OF CARDS. Rows are roster slots in
 * rosterSlots order; columns are PRIMARY / BACKUP 1 / BACKUP 2 / BACKUP 3 on
 * the PLAN_GRID template SlotRow exports, so every row aligns with the sticky
 * column headers down the whole page. Bench rows are full size — see SlotRow.
 *
 * Nothing mounts this yet; wiring is chunk 3.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { theme } from "../../data/theme";
import OnboardingSheet from "../OnboardingSheet";
import { LeagueContextLine } from "../LeagueChip";
import { getEffectivePosRank } from "../../data/adpData";
import { makePpgInfo, sumPpg, formatPpgTotal, makePlayerSeasonPoints } from "./ppgTotals";
import PlanSummary from "./PlanSummary";
import OptimizeSheet from "./OptimizeSheet";
import useBaselinePlan from "./useBaselinePlan";
import SlotRow, { PLAN_GRID, PLAN_GAP } from "./SlotRow";
import { BUCKET_WIDTH, primaryOf, isGenerated, nextPlanName, planObjective, planBudgetMode, hasExplicitSlotBudget } from "../../hooks/usePlanState";
import PlayerPanel, { eligiblePositions } from "./PlayerPanel";
import useOptimizeActions from "./useOptimizeActions";
import DraftPlanPrint from "./DraftPlanPrint";
import PlanMenu from "./PlanMenu";
import PlanPicker from "./PlanPicker";
import PlanNameField from "./PlanNameField";
import PlayerSheet from "./PlayerSheet";
import { useIsNarrow, useIsWide, PHONE_BREAKPOINT } from "./useViewport";
import { capturePlanPrinted } from "../../utils/planAnalytics";

// Same literal-in-both-places convention the builder key uses.
// v3: v2 said whose numbers these are; v3 adds what you DO with them, which
// is lock targets and optimize around them. v4 leads with the OUTCOME, a
// budget per position, and works an actual question through the loop: is Dak
// worth it at $10, set him to $10 and reoptimize. v5 is the same four ideas as
// a LIST, because four sentences of centred prose is a paragraph nobody
// finishes. Each bump re-shows the sheet once to everybody who dismissed the
// version before it.
const ONBOARDING_PLAN_KEY = "onboarding-draft-plan-v5";

// Below this the panel cannot sit beside a four-column matrix without one of
// them being unusable, so the grid goes full width and the panel drops under it.
const PANEL_BREAKPOINT = 1340;

/**
 * 🔴 DRAFT PLAN IS WIDER THAN THE REST OF THE APP, and only Draft Plan.
 *
 * Four columns plus a player panel is the widest surface here; every other tab
 * is a normal page and stays at the app's 1400. The 1400 cap is what forced the
 * panel row onto two lines and clamped player names to ~13 characters.
 *
 * Capped at 1880 rather than uncapped: past roughly 1900 a four-column grid puts
 * the rail and Plan D further apart than the eye wants to travel, and the panel
 * drifts away from the row it is filling.
 */
const PLAN_MAX_WIDTH = 1880;

/**
 * Above this the panel gets 480 instead of 372, which is enough for its row to
 * fit on ONE line.
 *
 * 1700, not 1512: at 1512 a 480 panel leaves the grid 972px — 214 per cell,
 * exactly what it has today — so the panel would win its extra width straight
 * out of the grid's name clamp. A 1366 or 1512 laptop gets no extra pixels and
 * keeps the two-line row, which is why that layout stays.
 */
const WIDE_PANEL_BREAKPOINT = 1700;
// 540, not 480: the breakeven column needs ~58px, and at 480 it would have
// taken them out of the name (17 chars -> 10). Measured before building — at
// 540 the name is 18 chars, BETTER than before the column existed, and the
// grid cell only falls 306 -> 291 (24 chars, still clear of the longest name).
const PANEL_WIDTH_WIDE = 540;
const PANEL_WIDTH = 372;

// 🔴 THE COLUMNS ARE PLANS, NOT BACKUPS. Reading DOWN a column is a whole
// draftable roster; reading ACROSS a row is your options at that slot. Only
// column A counts against the budget — see the scope line under the summary.
//
// 🔴 TWO ARRAYS, AND THE SPLIT IS THE PRINT SHEET'S. On screen PRIMARY is a
// pill beside PLAN A, which is a stronger signal than four more characters of
// the same faint caption and reads at a glance from across the row. Paper has
// no pill and no colour to spare, so the print header keeps the words it has
// always had and its output is byte-identical.
const COLUMN_LABELS = ["PLAN A", "PLAN B", "PLAN C", "PLAN D"];
const PRINT_COLUMN_LABELS = ["PLAN A · PRIMARY", "PLAN B", "PLAN C", "PLAN D"];

function ToolbarButton({ children, onClick, active, danger, disabled, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        opacity: disabled ? 0.45 : 1,
        padding: `${theme.spacing.sm}px ${theme.spacing.lg}px`,
        borderRadius: theme.radius.md,
        border: `1px solid ${active ? theme.colors.border.brand : theme.colors.border.default}`,
        background: active ? theme.helpers.brandBg(0.12) : theme.colors.bg.raised,
        color: danger ? theme.colors.status.danger : (active ? theme.colors.brand.orange : theme.colors.text.secondary),
        fontSize: theme.fontSize.sub,
        fontWeight: theme.fontWeight.bold,
        fontFamily: theme.fonts.body,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >{children}</button>
  );
}

/**
 * 🔴 THE OBJECTIVE AND BUDGET-MODE CONTROLS MOVED INTO OptimizeSheet, chips and
 * copy together, when the toolbar collapsed to one button. They are settings
 * that only matter next to the GO they modify, and the strings live with them.
 */

/**
 * The subtotal rides ON the heading rather than in a row of its own — vertical
 * space is the scarce thing on this page and a whole extra row per group buys
 * nothing a heading cannot carry. Secondary/faint ink so the heading still
 * reads as a heading and the figures as an annotation on it.
 */
function GroupHeading({ children, count, group, ppg, unprojected = 0 }) {
  return (
    <div style={{
      display: "flex", alignItems: "baseline", gap: theme.spacing.md, flexWrap: "wrap",
      marginTop: theme.spacing.xxl, marginBottom: theme.spacing.md,
    }}>
      <span style={{
        fontFamily: theme.fonts.display,
        fontSize: theme.fontSize.small,
        fontWeight: theme.fontWeight.heavy,
        letterSpacing: 1.2,
        color: theme.colors.text.secondary,
      }}>{children}</span>
      <span style={{ fontSize: theme.fontSize.small, color: theme.colors.text.faint }}>
        · {count} spots
        {group ? ` · planned $${Math.round(group.planned)} · targeted $${Math.round(group.targeted)}` : ""}
        {ppg > 0 || unprojected > 0 ? ` · ${formatPpgTotal(ppg, unprojected)} PPG` : ""}
      </span>
    </div>
  );
}

/** The plan as a paste-able block: one line per slot, backups in brackets. */
export function planAsText(plan, rosterSlots, primaryFor) {
  const lines = [`${plan.name} — $${plan.budget} budget`, ""];
  rosterSlots.forEach(slot => {
    const bucket = plan.buckets[slot.id];
    const primary = primaryFor(slot.id);
    const backups = ((bucket && bucket.cells) || [])
      .filter((c, i) => c && i > 0 && c !== primary)
      .map(c => `${c.name} $${c.price}`);
    const head = primary ? `${primary.name} $${primary.price}` : "—";
    lines.push(`${slot.label.padEnd(5)} ${head}${backups.length ? `   [${backups.join(" | ")}]` : ""}`);
  });
  return lines.join("\n");
}

/**
 * `planApi` is the usePlanState return (state + actions); `plan` is the active
 * Plan object off it. Two props for what is arguably one thing, because the
 * shell already holds both and passing the active plan explicitly keeps the
 * "which plan am I looking at" question answerable without reaching into the
 * API object at every read site.
 */
export default function DraftPlan({
  plan,
  planApi,
  rosterSlots,
  dynamicPrices,
  activeLeagueName,
  onSetup,
  getPlayerProj,
  getPlayerGames,
  getPlayerVorp,
  settings,
}) {
  const {
    plans, activePlanId, setActivePlanId,
    buckets, activeCell, setActiveCell,
    primaryFor, planned, targeted, secured, lockedSlots,
    filled, committed, emptySlots, unplanned, remaining, maxBid, spendingByPos, byGroup,
    addTarget, removeTarget, promoteTarget, toggleGone, toggleLock, setTargetPrice,
    setSlotBudget, fillSlotBudgetsFromTargets, resyncMarket,
    newPlan, duplicatePlan, renamePlan, deletePlan, setPlanBudget, setPlanObjective, setPlanBudgetMode, clearPlan, exportPlans,
    createFilledPlans, deleteGeneratedPlans, keepOnlyActivePlan,
    leaguePrices, setSheetPrice, clearSheetPrices,
    leaguePpg, setSheetPpg, clearSheetPpg,
    leagueStars, toggleStar, clearStars,
  } = planApi;
  const activePlan = plan || planApi.activePlan;

  const [hidePlayers, setHidePlayers] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  // Informational, not an error — see the render site.
  const [toolbarNote, setToolbarNote] = useState(null);

  /**
   * 🔴 THE FIRST FLIP INTO PER SPOT SEEDS THE RAILS, and only the first.
   * Without this the mode is technically live and practically inert: it binds
   * only rails the user typed, so a fresh plan switches to PER SPOT and behaves
   * exactly like TOTAL until sixteen numbers are entered by hand. Seeding from
   * the targets already on the plan makes the mode mean something immediately.
   *
   * 🔴 AND IT NEVER OVERWRITES. One explicit rail anywhere means the user has
   * started doing this themselves, and a helper that silently rewrites their
   * number is worse than one that does nothing. "From targets" is the explicit
   * way to overwrite, which is why it is a button and this is not.
   */
  /**
   * 🔴 THE DEFAULT EVERY EMPTY STARTER RAIL SHOWS, and the number the seed
   * writes into it. One optimizer run on an empty plan, memoised on prices and
   * settings rather than on the plan, so it does not move while you work. See
   * useBaselinePlan.
   */
  const { baselinePriceOf } = useBaselinePlan({
    rosterSlots,
    settings,
    budget: activePlan ? activePlan.budget : 0,
    dynamicPrices,
    priceSheet: leaguePrices,
    ppgOverrides: leaguePpg,
    getPlayerProj,
    getPlayerGames,
  });

  const onBudgetModeChange = useCallback((mode) => {
    setPlanBudgetMode(mode);
    if (mode !== "slot" || hasExplicitSlotBudget(activePlan)) { setToolbarNote(null); return; }
    fillSlotBudgetsFromTargets(baselinePriceOf);
    setToolbarNote("Filled every spot's budget: your targets where you have one, the board's target for that spot where you do not. Edit any of them.");
  }, [activePlan, setPlanBudgetMode, fillSlotBudgetsFromTargets, baselinePriceOf]);
  const [copied, setCopied] = useState(false);
  const narrow = useIsNarrow(PANEL_BREAKPOINT);
  const widePanel = useIsWide(WIDE_PANEL_BREAKPOINT);
  const phone = useIsNarrow(PHONE_BREAKPOINT);
  /**
   * 🔴 THE TOGGLE'S STATE ONLY COUNTS WHILE THE TOGGLE IS ON SCREEN. Hiding the
   * players on a desktop and then narrowing to a phone would otherwise leave
   * the panel gone with no control anywhere on the page to bring it back — an
   * unreachable state reachable by turning a tablet sideways.
   */
  const playersHidden = hidePlayers && !phone;
  /**
   * 🔴 WHICH COLUMN THE PHONE IS SHOWING. VIEW STATE, and deliberately not on
   * the plan: it is not a decision about the roster, it does not survive a
   * reload, and putting it on the plan would drag it into the optimizer's
   * fingerprint and mark a plan stale for scrolling sideways. It also never
   * moves the budget — PLANNED, TARGETED, LEFT and MAX BID track column A at
   * every tab, exactly as they do when all four are on screen.
   *
   * Defaults to 0, the primary column, because that is the one the money is
   * about and the one a plan is normally read as.
   */
  const [phoneColumn, setPhoneColumn] = useState(0);

  /**
   * 🔴 THE PLAYER LIST IS A SHEET ON A PHONE, AND THIS IS THE BUG IT FIXES.
   * Below PANEL_BREAKPOINT the panel drops under the matrix, so tapping a cell
   * selected it, re-scoped the panel to that slot, and put all of that sixteen
   * roster rows off screen. It read as a dead tap. Below 640 the panel is not
   * rendered inline at all; it opens over the page instead, already scoped.
   *
   * View state, like phoneColumn: nothing here reaches the plan, and dismissing
   * is always free because adding a player is the only path that writes.
   */
  const [sheetOpen, setSheetOpen] = useState(false);
  const openSheetFor = useCallback((slotId, cellIdx) => {
    setActiveCell({ slotId, cellIdx });
    setSheetOpen(true);
  }, [setActiveCell]);
  const generatedPlans = useMemo(() => plans.filter(isGenerated), [plans]);
  const generatedCount = generatedPlans.length;

  // 🔴 The solver orchestration lives in its own hook. This file composes.
  const {
    optimizeError, optimizeNote, handleOptimize, optimizeRemaining,
    rebuildEverything, isStale, isFresh, commitmentCosts,
  } = useOptimizeActions({
    activePlan, rosterSlots, dynamicPrices, settings,
    getPlayerProj, getPlayerGames, getPlayerVorp, createFilledPlans,
    priceSheet: leaguePrices, ppgOverrides: leaguePpg,
  });

  // 🔴 Fires on BOTH the board moving and the plan switching. resyncMarket is
  // scoped to the active plan, so a plan that was not active when the board
  // moved keeps stale `market` values — and market is exactly what the delta
  // pill compares against, so a stale plan renders ±0 against a price that has
  // since changed. Firing on plan switch closes that lazily, at the moment the
  // plan becomes visible.
  //
  // No render loop: updateActive depends only on activePlanId, and resyncMarket
  // only on updateActive + dynamicPrices, so calling it does not change its own
  // identity and cannot re-trigger this effect.
  useEffect(() => { resyncMarket(); }, [dynamicPrices, activePlanId, resyncMarket]);

  const starters = useMemo(() => rosterSlots.filter(s => s.pos !== "BN"), [rosterSlots]);
  const bench = useMemo(() => rosterSlots.filter(s => s.pos === "BN"), [rosterSlots]);

  // Every key already in this plan, so the panel can dim what is spoken for.
  const plannedKeys = useMemo(() => {
    const keys = new Set();
    Object.values(buckets).forEach(b => (b.cells || []).forEach(c => { if (c) keys.add(c.key); }));
    return keys;
  }, [buckets]);

  /**
   * PPG for a target — the ONE definition in this feature.
   *
   * getPlayerProj (usePricing) returns SEASON points over the games THAT
   * projection was normalised to, and getPlayerGames returns those games — so
   * PPG is points ÷ his own games, not points ÷ 17. There is deliberately no
   * second projection path here.
   *
   * A Target carries only name/pos/team, so the real record is resolved by key
   * first and the Target used as a fallback on a miss — projectPoints scores
   * that as 0, which callers render as nothing rather than "0.0 PPG".
   */
  // 🔴 THE SOLVER'S CURRENCY IS SEASON POINTS, NOT THE RATE ABOVE. Breakeven runs
  // the same objective the Optimize buttons do, so a breakeven answers the
  // question those buttons would answer — and both maximise season points, which
  // is what a roster actually scores. See makePlayerSeasonPoints.
  const seasonObjective = useMemo(
    () => makePlayerSeasonPoints(getPlayerProj, getPlayerGames, leaguePpg),
    [getPlayerProj, getPlayerGames, leaguePpg]
  );

  // Override, else engine — for cards, both group totals and the printed sheet.
  const ppgInfo = useMemo(
    () => makePpgInfo(getPlayerProj, getPlayerGames, leaguePpg),
    [getPlayerProj, getPlayerGames, leaguePpg]
  );
  // The card only ever needs the number; it already renders nothing at 0.
  const ppgFor = useCallback((target) => ppgInfo(target).ppg, [ppgInfo]);

  /**
   * 🔴 THE ENGINE'S RATE, WITH NO OVERRIDES APPLIED — the same distinction
   * PlayerPanel draws, for the same reason. This is what the card's × resets
   * TO, so folding the override into it would make it agree with itself and
   * stop being a reference: pressing × would restore the number you were trying
   * to remove.
   */
  const enginePpgInfo = useMemo(
    () => makePpgInfo(getPlayerProj, getPlayerGames),
    [getPlayerProj, getPlayerGames]
  );
  const enginePpgFor = useCallback((target) => enginePpgInfo(target).ppg, [enginePpgInfo]);
  const ppgOverrideFor = useCallback((target) => (target ? leaguePpg[target.key] : undefined), [leaguePpg]);

  /**
   * 🔴 STARTERS ONLY, and computed HERE rather than in planTotals. planTotals
   * is pure over (plan, rosterSlots) with no pricing dependency and is what
   * planBudget.test.js pins; handing it getPlayerProj would couple the tested
   * budget core to the projection engine for one display number.
   *
   * Bench excluded because a bench player scores you nothing — summing them
   * would produce a number that looks like a lineup total and is not one.
   */
  //
  // 🔴 AND IT CARRIES AN `unprojected` COUNT. An unresolvable primary scores 0,
  // and adding that 0 in silently makes the total read as a complete lineup
  // while omitting a player — wrong in the one direction nobody can spot,
  // because it still looks plausible. See ppgTotals.
  const groupPpg = useMemo(() => rosterSlots.reduce((acc, slot) => {
    const primary = primaryFor(slot.id);
    if (!primary) return acc;
    const bucket = slot.pos === "BN" ? "bench" : "starters";
    const { ppg, projected } = ppgInfo(primary);
    acc[bucket].ppg += ppg;
    if (!projected) acc[bucket].unprojected += 1;
    return acc;
  }, { starters: { ppg: 0, unprojected: 0 }, bench: { ppg: 0, unprojected: 0 } }),
  [rosterSlots, primaryFor, ppgInfo]);

  // The summary tile is the starters half of the same split.
  const startersPpg = groupPpg.starters.ppg;
  const startersUnprojected = groupPpg.starters.unprojected;

  /**
   * Cost and starters PPG for each COLUMN — each of which is now a whole
   * roster. Computed live off the buckets rather than stored with the run, so
   * hand-editing a cell keeps the header honest.
   *
   * The degradation across columns is real information: if Plan D reads 110 PPG
   * against Plan A's 137, the pool behind your targets is thin. It is not
   * smoothed or hidden.
   */
  const columnTotals = useMemo(() => Array.from({ length: BUCKET_WIDTH }, (_, cellIdx) => {
    let cost = 0, count = 0;
    const starters = [];
    rosterSlots.forEach((slot) => {
      const bucket = buckets[slot.id];
      const target = bucket && bucket.cells ? bucket.cells[cellIdx] : null;
      if (!target) return;
      count += 1;
      cost += Number(target.price) || 0;
      if (slot.pos !== "BN") starters.push(target);
    });
    const { ppg, unprojected } = sumPpg(starters, ppgInfo);
    return { cost, ppg, count, unprojected };
  }), [rosterSlots, buckets, ppgInfo]);

  /**
   * Where would this player go if you added him with no cell selected?
   *
   * 🔴 THE FIRST OPEN *PRIMARY*, NOT THE FIRST OPEN CELL. Falling back to any
   * empty cell would happily drop him into BN6's third backup, where he is
   * technically placed and effectively vanished — the user clicks +, the list
   * dims the row, and nothing they can see has changed. A primary is the only
   * placement that is visible and that counts against the budget.
   *
   * Returns the slot, or null when there is nowhere sensible. Null disables the
   * + rather than picking somewhere surprising.
   */
  const placementFor = useCallback((player) => {
    if (!player) return null;
    return rosterSlots.find((slot) => {
      if (!eligiblePositions(slot, settings ? settings.positions : undefined).includes(player.pos)) return false;
      const bucket = buckets[slot.id];
      return !bucket || !bucket.cells || !bucket.cells[0];
    }) || null;
  }, [rosterSlots, buckets, settings]);

  // Over the spots that still need MONEY, not over every open spot: under PER
  // SPOT a rail already covers its own slot out of PLANNED. Identical to
  // `emptySlots` in total mode, where an earmarked slot is not open.
  const avgPerOpen = unplanned > 0 ? remaining / unplanned : 0;
  const activeSlot = activeCell ? rosterSlots.find(s => s.id === activeCell.slotId) : null;
  const posRankFor = (target) => getEffectivePosRank(target.name, null) || target.pos;

  /**
   * Print, with the capture FIRST.
   *
   * 🔴 window.print() IS SYNCHRONOUS AND BLOCKS THE THREAD until the user
   * dismisses the dialog. A capture after it would sit behind that block, and
   * on a tab the user closes from the print preview it would never be sent at
   * all. Queue the event, then hand over the thread.
   *
   * ⚠️ A CANCELLED DIALOG STILL COUNTS. That is a deliberate over-count of
   * intent: reaching for Print is the signal worth having, and the honest
   * alternative (an afterprint listener) is not fired at all by several
   * browsers, which trades a small over-count for a large silent under-count.
   */
  const handlePrint = useCallback(() => {
    capturePlanPrinted({
      slotsFilled: rosterSlots.filter(s => primaryOf(activePlan?.buckets?.[s.id])).length,
      planCount: plans.length,
    });
    window.print();
  }, [rosterSlots, activePlan, plans.length]);

  const handleCopy = () => {
    const text = planAsText(activePlan, rosterSlots, primaryFor);
    try {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard unavailable */ }
  };

  const handleExport = () => {
    try {
      const blob = new Blob([JSON.stringify(exportPlans(), null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "draft-plans.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* download unavailable */ }
  };

  const isOptimizedPlan = !!(activePlan && activePlan.optimized);

  /**
   * Which column the slot's effective primary actually came from.
   *
   * > 0 means primaryOf fell through — cell 0 is empty or marked gone, so the
   * budget is now charging you for Plan B/C/D's pick at this slot while the
   * rest of the roster is still Plan A. Correct behaviour (see primaryOf), but
   * worth saying out loud, and only meaningful once the columns ARE plans.
   */
  const fallThroughIdxFor = (slot) => {
    if (!isOptimizedPlan) return -1;
    const bucket = buckets[slot.id];
    const primary = primaryOf(bucket);
    if (!primary || !bucket || !bucket.cells) return -1;
    return bucket.cells.indexOf(primary);
  };

  // One league sheet, read by key — so a star shows on every plan and every
  // column holding that player, with no per-cell copy to keep in sync.
  const isStarred = useCallback((key) => !!(leagueStars && leagueStars[key]), [leagueStars]);

  const rowProps = (slot) => ({
    slot,
    bucket: buckets[slot.id],
    fallThroughIdx: fallThroughIdxFor(slot),
    commitmentCost: commitmentCosts[slot.id] || 0,
    onReoptimize: handleOptimize,
    activeCell,
    posRankFor,
    ppgFor,
    isStarred,
    differs: !!(activePlan && activePlan.optimized && activePlan.optimized.differs.includes(slot.id)),
    // 🔴 SAME SELECTION EITHER WAY. The phone branch adds the sheet, it does
    // not replace the activeCell write — the destination the panel adds into is
    // the one this sets, on both layouts.
    onSelectCell: (cellIdx) => {
      if (phone) { openSheetFor(slot.id, cellIdx); return; }
      setActiveCell({ slotId: slot.id, cellIdx });
    },
    onPromote: (cellIdx) => promoteTarget(slot.id, cellIdx),
    onToggleGone: (cellIdx) => toggleGone(slot.id, cellIdx),
    // 🔴 KEYED BY PLAYER, LIKE THE STAR AND THE PRICE — the cell only says WHICH
    // player. A PPG override is a view about a person, so it is league-wide and
    // lands in the same sheet the player list writes; the line under the column
    // headers says so on screen.
    ppgOverrideFor,
    enginePpgFor,
    onPpgChange: (cellIdx, value) => {
      const cell = (buckets[slot.id] && buckets[slot.id].cells) || [];
      if (cell[cellIdx]) setSheetPpg(cell[cellIdx].key, value);
    },
    onPpgReset: (cellIdx) => {
      const cell = (buckets[slot.id] && buckets[slot.id].cells) || [];
      if (cell[cellIdx]) setSheetPpg(cell[cellIdx].key, null);
    },
    // Takes a cellIdx like its neighbours; the promote-then-lock is the hook's
    // job, not this file's — see toggleLockInBucket.
    onToggleLock: (cellIdx) => toggleLock(slot.id, cellIdx),
    // 🔴 Takes a cellIdx like its neighbours, and resolves it to the KEY here.
    // A star is a property of the player, so the cell only says WHICH player —
    // starring cell 0 of RB1 and cell 2 of BN3 for the same man is one star.
    onToggleStar: (cellIdx) => {
      const cell = (buckets[slot.id] && buckets[slot.id].cells) || [];
      if (cell[cellIdx]) toggleStar(cell[cellIdx].key);
    },
    onRemove: (cellIdx) => removeTarget(slot.id, cellIdx),
    onPriceChange: (cellIdx, value) => setTargetPrice(slot.id, cellIdx, value),
    onBudgetChange: (value) => setSlotBudget(slot.id, value),
    // Display only: the rail shows this muted when the slot is empty and
    // carries no earmark of its own. Nothing in planTotals reads it.
    baselinePrice: baselinePriceOf(slot.id),
    // null on a desktop: draw all four. See SlotRow — the cells that ARE drawn
    // keep their true bucket index either way.
    onlyCellIdx: phone ? phoneColumn : null,
    // 🔴 A FILLED CARD IS ALSO A WAY IN, ON A PHONE ONLY. There is no add
    // affordance on a filled row: the promote / gone / remove cluster is
    // hover-gated and never appears on touch, so a row that already has a
    // player had no route to the list at its own slot. Tapping the card body
    // selects it and opens the sheet. Desktop passes false and the filled card
    // keeps having no root click handler at all, exactly as before.
    selectOnTap: phone,
  });

  /**
   * 🔴 ONE PROP BAG, TWO PLACEMENTS. The desktop renders this panel in the
   * right-hand column and the phone renders the SAME panel inside PlayerSheet.
   * Written once because the list on a phone must be the list on a desktop —
   * two prop lists would drift the first time either grew a prop, and the one
   * that drifted would be the phone's, where nobody is looking.
   */
  const panelProps = {
    slot: activeSlot,
    cellIdx: activeCell ? activeCell.cellIdx : 0,
    dynamicPrices,
    plannedKeys,
    leaguePrices,
    onSetSheetPrice: setSheetPrice,
    onClearSheetPrices: clearSheetPrices,
    leaguePpg,
    onSetSheetPpg: setSheetPpg,
    onClearSheetPpg: clearSheetPpg,
    leagueStars,
    onToggleStar: toggleStar,
    onClearStars: clearStars,
    getPlayerProj,
    getPlayerGames,
    left: remaining,
    maxBid,
    placementFor,
    positions: settings ? settings.positions : undefined,
    wide: widePanel,
    valueOf: seasonObjective,
    budget: activePlan ? activePlan.budget : 0,
    rosterSlots,
  };

  // With a cell selected, fill it. Without one, the panel is a browser and +
  // means "put him somewhere I will actually see".
  const addFromPanel = (player) => {
    if (activeCell) { addTarget(activeCell.slotId, activeCell.cellIdx, player); return; }
    const slot = placementFor(player);
    if (slot) addTarget(slot.id, 0, player);
  };

  // "RB1 primary" / "WR2 backup 2" / "All players" — what the sheet is scoped
  // to, said at the top of it, because on a phone the cell you tapped is behind
  // the sheet and cannot be checked.
  const sheetTitle = activeSlot && activeCell
    ? `${activeSlot.label} ${activeCell.cellIdx === 0 ? "primary" : `backup ${activeCell.cellIdx}`}`
    : "All players";

  return (
    <>
    {/* The paper version. Its own markup from the same data — display:none on
        screen, and the only thing visible in print (the .dp-root tree below is
        hidden then). See DraftPlanPrint for why print does not reuse this DOM. */}
    <DraftPlanPrint
      planName={activePlan ? activePlan.name : ""}
      leagueName={activeLeagueName}
      cap={activePlan ? activePlan.budget : 0}
      planned={planned}
      targeted={targeted}
      left={remaining}
      startersPpg={startersPpg}
      startersUnprojected={startersUnprojected}
      rosterSlots={rosterSlots}
      buckets={buckets}
      byGroup={byGroup}
      groupPpg={groupPpg}
      columnTotals={columnTotals}
      columnLabels={PRINT_COLUMN_LABELS}
      isStarred={isStarred}
    />

    <div className="dp-root" style={{ width: "100%", maxWidth: PLAN_MAX_WIDTH, margin: "0 auto", padding: theme.spacing.xxl }}>

      <LeagueContextLine
        text="Planning for"
        name={activeLeagueName}
        onSetup={onSetup}
        setupSource="draft_plan"
        style={{ marginBottom: theme.spacing.sm }}
      />

      {/* 🔴 THE BETA LINE, AND IT SAYS WHERE THE NUMBERS CAME FROM. Every
          price on this page is seeded from the board (dynamicPrices), which is a
          market estimate rather than anything a user typed. Somebody who does not
          know that reads an editable field as their own input and never questions
          it. Saying it once, here, is what makes the pencil icons legible.

          The invite is a mailto, the same one ResultsScreen uses, because a beta
          that asks for corrections and gives you nowhere to send them is asking
          for nothing. */}
      <div style={{
        fontSize: theme.fontSize.sub,
        color: theme.colors.text.muted,
        marginBottom: theme.spacing.lg,
      }}>
        Prices auto-fill from the board and stay editable.{" "}
        <a
          href="mailto:mock1app@gmail.com?subject=Draft%20Plan%20prices"
          style={{ color: theme.colors.brand.accent, textDecoration: "none" }}
        >Tell me what&rsquo;s off &rarr;</a>
      </div>

      <div>
        <OnboardingSheet
          storageKey={ONBOARDING_PLAN_KEY}
          section="Draft Plan"
          headline="Go into your auction with a budget for each position."
          subtitle={[
            "See what each player is worth at your league's settings, then pick your targets.",
            "Set YOUR OWN prices and projected PPG and Mock1 reoptimizes around them. Is Dak still worth it at $10? Set him to $10 and reoptimize. Only 20 PPG? Set that and reoptimize again.",
            "Hit the lock icon to lock a player in at a price and reoptimize around any number of locked players.",
            "Optimize with a budget at each position, or let Mock1 find the best lineup from your prices and projections.",
          ]}
        />
      </div>

      <div style={{ marginTop: theme.spacing.lg }}>
        <PlanSummary
          cap={activePlan ? activePlan.budget : 0}
          secured={secured}
          lockedSlots={lockedSlots}
          planned={planned}
          targeted={targeted}
          left={remaining}
          avg={avgPerOpen}
          maxBid={maxBid}
          filled={filled}
          committed={committed}
          total={rosterSlots.length}
          emptySlots={emptySlots}
          unplanned={unplanned}
          budgetMode={planBudgetMode(activePlan)}
          byPos={spendingByPos}
          onCapChange={setPlanBudget}
          startersPpg={startersPpg}
          startersUnprojected={startersUnprojected}
        />
      </div>

      {/* 🔴 THE CAVEAT THAT HAS TO SIT NEAR THE NUMBERS. Every commitment cost
          is measured with the REST of the roster at board prices, so it answers
          "what would this cost if the market behaved" — not "what will this
          cost". It matters more here than for the optimizer itself, because a
          figure to one decimal place reads as far more certain than it is. */}
      {Object.keys(commitmentCosts).length > 0 && (
        <div style={{
          marginTop: theme.spacing.md,
          fontSize: theme.fontSize.sub,
          color: theme.colors.text.muted,
        }}>
          Commitment costs assume everyone else goes for the board price.
        </div>
      )}

      {/* 🔴 WHICH COLUMN THE BUDGET IS ABOUT. With four full rosters on screen,
          PLANNED / TARGETED / LEFT reading only column 0 is genuinely
          confusing — the numbers look like they should cover what you can see.
          Shown whenever a second column has anything in it, optimized or not. */}
      {columnTotals[1] && columnTotals[1].count > 0 && (
        <div style={{
          marginTop: theme.spacing.md,
          fontSize: theme.fontSize.sub,
          color: theme.colors.text.muted,
        }}>
          Budget totals track Plan A. B, C and D are alternatives, not additional spend.
        </div>
      )}

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      {/* 🔴 UNDER THE SUMMARY, NOT ABOVE IT. The page opens with what the plan
          IS - the league it is for, then the money and the points - and only
          then with the controls that change it. The toolbar led for as long as
          it was the only thing on the page; once the summary grew two heroes
          and a spend bar, leading with a name field and a menu buried the
          answer under the machinery. The banners stay attached to it, because
          every one of them is a reply to something pressed here. */}
      {/* 🔴 ONE ROW ON A PHONE, WITH THE NAME PAYING FOR IT. Four controls at
          their natural widths are about 420px and the row is 372px, so wrapping
          put OPTIMIZE — the only thing most people came here to press — alone on
          a second line under a mostly empty first one. nowrap plus a flexing
          name field spends the shortfall on the one control that can lose
          characters and still be read. */}
      <div style={{
        display: "flex", alignItems: "center",
        gap: phone ? theme.spacing.sm : theme.spacing.md,
        flexWrap: phone ? "nowrap" : "wrap",
        // The gap that used to sit above the matrix, now sitting above the
        // toolbar: the summary is the block it has to be separated from.
        marginTop: theme.spacing.xl,
        // 🔴 AND THIS ONE IS THE GAP TO THE COLUMN HEADERS. The matrix carries
        // no top margin of its own any more, because the toolbar and its
        // banners are what sit above it and each of them ends in this token.
        marginBottom: theme.spacing.lg,
      }}>
        {/* 🔴 THE NAME AND THE SWITCHER ARE TWO CONTROLS, side by side and never
            the same pixel — see PlanNameField. The name is where you rename;
            the caret is where you switch. `npm run toolbar-check` lays this row
            out in headless Chrome and fails if the two boxes overlap or if a
            long name pushes the toolbar onto a second line. */}
        <PlanNameField
          flexible={phone}
          name={activePlan ? activePlan.name : ""}
          onCommit={renamePlan}
        />
        {/* Two groups, off the same flag the sweep filters on. See PlanPicker. */}
        <PlanPicker
          compact
          plans={plans}
          value={activePlanId || (activePlan && activePlan.id) || ""}
          onChange={setActivePlanId}
        />

        {/* Seven rare operations behind one affordance. See PlanMenu. */}
        <PlanMenu
          planName={activePlan ? activePlan.name : "plan"}
          planCount={plans.length}
          generatedCount={generatedCount}
          generatedNames={generatedPlans.map((p) => p.name)}
          onNew={() => newPlan(nextPlanName(plans))}
          onDuplicate={() => duplicatePlan(`${activePlan ? activePlan.name : "Plan"} copy`)}
          onRename={() => {
            const next = window.prompt("Rename this plan", activePlan ? activePlan.name : "");
            const trimmed = next == null ? null : next.trim();
            if (trimmed) renamePlan(trimmed);
          }}
          onDelete={() => deletePlan(activePlanId || (activePlan && activePlan.id))}
          onDeleteGenerated={() => {
            setToolbarNote(null);
            const removed = deleteGeneratedPlans();
            // Never a silent no-op: the flag-based sweep is blind to plans made
            // before the flag existed, and the user has to be told that rather
            // than left wondering why nothing happened.
            if (removed === 0) {
              setToolbarNote("No optimizer-generated plans to remove. Plans made before this feature carry no marker — use \u201CKeep only this plan\u201D to clear a pile of those.");
            }
          }}
          onKeepOnly={() => { setToolbarNote(null); keepOnlyActivePlan(); }}
          onExport={handleExport}
        />

        {/* 🔴 ONE BUTTON, THREE QUESTIONS BEHIND IT. This row used to carry
            five controls: two settings dressed as buttons and three actions,
            with the settings CROSSING the actions and nothing on the row saying
            "press this to optimize" - which is the only thing most people came
            here to do. The sheet asks the three questions in the order you
            would ask them out loud and pre-selects every one from the plan, so
            the fast path is OPTIMIZE then GO. See OptimizeSheet. */}
        <OptimizeSheet
          objective={planObjective(activePlan)}
          onObjectiveChange={setPlanObjective}
          budgetMode={planBudgetMode(activePlan)}
          onBudgetModeChange={onBudgetModeChange}
          budget={activePlan ? activePlan.budget : 0}
          onFillFromTargets={() => { setToolbarNote(null); fillSlotBudgetsFromTargets(baselinePriceOf); }}
          isStale={isStale}
          onFill={optimizeRemaining}
          onRebuild={rebuildEverything}
        />

        {/* Both the spacer and the toggle are desktop-only, and they go
            together: with nothing to push right, a flex:1 spacer would just
            compete with the name field for the row's remainder. */}
        {!phone && <div style={{ flex: 1 }} />}

        {/* Stays top-level: a view toggle you press while working, not a plan
            operation. Copy plan as text, Print and Clear plan are in the
            footer, where the things you do when you are finished live.

            🔴 NOT RENDERED BELOW 640. The panel already drops under the matrix
            at any width below PANEL_BREAKPOINT, so on a phone this button hides
            something that is not beside anything — it buys no width back and
            costs a control on the row where controls are scarcest. */}
        {!phone && (
          <ToolbarButton active={hidePlayers} onClick={() => setHidePlayers(v => !v)}>
            {hidePlayers ? "Show players" : "Hide players"}
          </ToolbarButton>
        )}
      </div>

      {/* Inline, next to the button that produced it. Not an alert — that
          blocks the page and loses the text on dismiss — and never a silent
          no-op, which is what makes a button feel broken. */}
      {/* Informational, not an error: muted rather than danger, because "there
          was nothing to remove" is an answer, not a failure. Rendered in the
          same slot so there is one place to look after clicking anything. */}
      {toolbarNote && (
        <div style={{
          marginBottom: theme.spacing.lg,
          padding: `${theme.spacing.md}px ${theme.spacing.lg}px`,
          borderRadius: theme.radius.md,
          borderLeft: `3px solid ${theme.colors.border.default}`,
          background: theme.colors.bg.deep,
          color: theme.colors.text.muted,
          fontSize: theme.fontSize.sub,
        }}>
          {toolbarNote}
        </div>
      )}

      {/* 🔴 A NOTE, NOT AN ERROR, and the distinction is the whole reason it
          is separate state. "Every slot is locked — nothing to optimize" is the
          correct answer for somebody whose draft is finished; rendering it in
          red under "Could not optimize:" would tell them their finished plan
          had failed. Same slot and same styling as toolbarNote, so there is
          still one place to look after pressing anything. */}
      {optimizeNote && (
        <div style={{
          marginBottom: theme.spacing.lg,
          padding: `${theme.spacing.md}px ${theme.spacing.lg}px`,
          borderRadius: theme.radius.md,
          borderLeft: `3px solid ${theme.colors.border.default}`,
          background: theme.colors.bg.deep,
          color: theme.colors.text.muted,
          fontSize: theme.fontSize.sub,
        }}>
          {optimizeNote}
        </div>
      )}

      {optimizeError && (
        <div style={{
          marginBottom: theme.spacing.lg,
          padding: `${theme.spacing.md}px ${theme.spacing.lg}px`,
          borderRadius: theme.radius.md,
          borderLeft: `3px solid ${theme.colors.status.danger}`,
          background: theme.colors.bg.deep,
          color: theme.colors.status.danger,
          fontSize: theme.fontSize.sub,
          fontWeight: theme.fontWeight.medium,
        }}>
          Could not optimize: {optimizeError}
        </div>
      )}

      {/* 🔴 SAY WHAT THE NUMBER MEANS. The solver maximises against BOARD
          prices, which are market estimates — not prices anybody can guarantee
          paying. Without this sentence the output reads as a promise rather
          than a plan, and the better the optimizer gets the more misleading
          that silence becomes. */}
      {activePlan && activePlan.optimized && (
        <div style={{ marginTop: -theme.spacing.md, marginBottom: theme.spacing.lg }}>
          {/* 🔴 THE QUIET STATE HAS TO SAY SOMETHING TOO. A button that is not
              warning you is indistinguishable from a button that has stopped
              checking, so the match gets stated as plainly as the mismatch.

              🔴 AND IT DOES NOT OVERCLAIM ONCE LOCKS EXIST. A plan holding
              purchases is optimal GIVEN those purchases — the solver never got
              to reconsider them, and one of them may well be the reason the
              rest of the roster is what it is. "Plan is optimal" would be a
              claim about a search that never ran. */}
          {isFresh && (
            <div style={{
              fontSize: theme.fontSize.sub,
              fontWeight: theme.fontWeight.bold,
              color: theme.colors.status.success,
              marginBottom: 2,
            }}>
              {lockedSlots > 0 ? "Optimal around your locks" : "Plan is optimal"}
            </div>
          )}
          {/* The finding leads. When the shapes match this says the two
              objectives agreed; when they do not it names the difference in
              positions, which is the actual result — the per-slot tags in the
              rails are the detail underneath it, not the headline. */}
          {activePlan.optimized.note && (
            <div style={{
              fontSize: theme.fontSize.sub,
              fontWeight: theme.fontWeight.medium,
              color: theme.colors.text.secondary,
              marginBottom: 2,
            }}>
              {activePlan.optimized.note}
            </div>
          )}
          <div style={{ fontSize: theme.fontSize.sub, color: theme.colors.text.muted }}>
            Best build if every player goes for the board price. Real auctions don&rsquo;t.
          </div>
        </div>
      )}

      {/* ── Matrix + panel ──────────────────────────────────────────────── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: (narrow || playersHidden)
          ? "1fr"
          : `1fr ${widePanel ? PANEL_WIDTH_WIDE : PANEL_WIDTH}px`,
        gap: theme.spacing.xxl,
        // No top margin: the toolbar above ends in one, and adjacent block
        // margins collapse, so a second token here would only argue with it.
        alignItems: "start",
      }}>
        <div>
          {/* 🔴 FOUR TABS INSTEAD OF FOUR COLUMNS BELOW 640. A 372px row cannot
              carry a rail and four target cards — each card would get 72px,
              which is not a name, a price and a PPG, it is a stripe. So the
              phone shows ONE column and the header row becomes the switch for
              which one. Every tab keeps the money and PPG line it has on a
              desktop, because that line is what you compare the columns ON and
              it is the only comparison left once three of them are off screen.

              Sticky, scrollable and content-sized: with four filled columns the
              tabs want a little more than the row has, and clipping the fourth
              plan is worse than a short sideways scroll. */}
          {phone ? (
            <div
              role="tablist"
              aria-label="Plan column"
              style={{
                position: "sticky", top: 0, zIndex: 5,
                display: "flex",
                gap: PLAN_GAP,
                overflowX: "auto",
                padding: `${theme.spacing.md}px 0`,
                background: theme.colors.bg.app,
                borderBottom: `1px solid ${theme.colors.border.subtle}`,
              }}
            >
              {COLUMN_LABELS.map((label, cellIdx) => {
                const t = columnTotals[cellIdx];
                const isPrimaryColumn = cellIdx === 0;
                const on = phoneColumn === cellIdx;
                return (
                  <button
                    key={label}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    data-testid={`plan-tab-${cellIdx}`}
                    onClick={() => setPhoneColumn(cellIdx)}
                    style={{
                      flex: "1 0 auto",
                      display: "flex", flexDirection: "column", alignItems: "center",
                      gap: 1,
                      padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
                      borderRadius: theme.radius.md,
                      border: `1px solid ${on ? theme.colors.border.brand : theme.colors.border.subtle}`,
                      background: on ? theme.helpers.brandBg(0.12) : theme.colors.bg.raised,
                      fontFamily: theme.fonts.body,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{
                        fontSize: theme.fontSize.caption,
                        fontWeight: theme.fontWeight.heavy,
                        letterSpacing: 0.9,
                        color: on ? theme.colors.brand.orange : theme.colors.text.faint,
                      }}>{label}</span>
                      {/* The same pill as the desktop header, and it stays even
                          on an unselected tab: which column the budget is about
                          is a fact about the plan, not about what you are
                          looking at. */}
                      {isPrimaryColumn && (
                        <span style={{
                          padding: "1px 5px",
                          borderRadius: theme.radius.sm,
                          border: `1px solid ${theme.colors.border.brand}`,
                          background: theme.helpers.brandBg(0.16),
                          color: theme.colors.brand.orange,
                          fontSize: theme.fontSize.micro,
                          fontWeight: theme.fontWeight.heavy,
                          letterSpacing: 0.6,
                        }}>PRIMARY</span>
                      )}
                    </span>
                    {/* Same rule as the desktop header: an empty column claiming
                        "$0 · 0.0 PPG" would read as a result. */}
                    {t.count > 0 && (
                      <span style={{ fontSize: theme.fontSize.caption, color: theme.colors.text.faint }}>
                        ${Math.round(t.cost)}
                        {(t.ppg > 0 || t.unprojected > 0) && (
                          <span style={{ color: theme.colors.text.secondary, fontWeight: theme.fontWeight.bold }}>
                            {` · ${formatPpgTotal(t.ppg, t.unprojected)} PPG`}
                          </span>
                        )}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            /* Column headers ride under the app header so the four columns stay
               identified however far down the roster you scroll. */
            <div style={{
              position: "sticky", top: 0, zIndex: 5,
              display: "grid",
              gridTemplateColumns: PLAN_GRID,
              gap: PLAN_GAP,
              padding: `${theme.spacing.md}px 0`,
              background: theme.colors.bg.app,
              borderBottom: `1px solid ${theme.colors.border.subtle}`,
            }}>
              <span />
              {COLUMN_LABELS.map((label, cellIdx) => {
                const t = columnTotals[cellIdx];
                const isPrimaryColumn = cellIdx === 0;
                return (
                  // 🔴 CENTRED OVER THE COLUMN, not left-aligned against its
                  // first pixel. The cards under it are a full-width grid track
                  // and the money sits on their right, so a label pinned left
                  // read as a label for the name rather than for the column.
                  <span key={label} style={{
                    display: "flex", flexDirection: "column", alignItems: "center",
                    gap: 1, textAlign: "center",
                  }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{
                        fontSize: theme.fontSize.caption,
                        fontWeight: theme.fontWeight.heavy,
                        letterSpacing: 0.9,
                        color: isPrimaryColumn ? theme.colors.text.secondary : theme.colors.text.faint,
                      }}>{label}</span>
                      {/* 🔴 A PILL, NOT FOUR MORE CHARACTERS OF THE SAME CAPTION.
                          Which column your money is actually committed to is the
                          one fact about this row somebody has to be able to read
                          from across the page, and "PLAN A · PRIMARY" in faint
                          ink said it in the same voice as PLAN D. */}
                      {isPrimaryColumn && (
                        <span style={{
                          padding: "1px 5px",
                          borderRadius: theme.radius.sm,
                          border: `1px solid ${theme.colors.border.brand}`,
                          background: theme.helpers.brandBg(0.16),
                          color: theme.colors.brand.orange,
                          fontSize: theme.fontSize.micro,
                          fontWeight: theme.fontWeight.heavy,
                          letterSpacing: 0.6,
                        }}>PRIMARY</span>
                      )}
                    </span>
                    {/* Only shown once a column has something in it — an empty
                        column claiming "$0 · 0.0 PPG" would read as a result. */}
                    {t.count > 0 && (
                      <span style={{ fontSize: theme.fontSize.caption, color: theme.colors.text.faint }}>
                        ${Math.round(t.cost)}
                        {/* 🔴 THE PPG CARRIES THE SECONDARY INK, THE COST STAYS
                            FAINT. Both have been on this line since the columns
                            became whole rosters, reading as one footnote; the
                            column's starters PPG is what you compare columns ON,
                            so it is the half that has to be legible at a glance.
                            The degradation across columns is real information: if
                            Plan D reads 110 against Plan A's 137, the pool behind
                            your targets is thin. */}
                        {(t.ppg > 0 || t.unprojected > 0) && (
                          <span style={{ color: theme.colors.text.secondary, fontWeight: theme.fontWeight.bold }}>
                            {` · ${formatPpgTotal(t.ppg, t.unprojected)} PPG`}
                          </span>
                        )}
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
          )}

          {/* 🔴 ONE LINE, BECAUSE THE SHEETS ARE LEAGUE-WIDE AND THE CARDS ARE
              NOT. Editing a price or a PPG on a card looks like editing THIS
              plan — the number is on this plan's card, under this plan's name —
              and it is not: both write a league sheet that every plan and the
              player list read. That is the intended design (one number, several
              places to type it), and it is exactly the kind of design that has
              to be said out loud rather than discovered by finding a plan you
              did not touch has changed. */}
          <div style={{
            fontSize: theme.fontSize.small,
            color: theme.colors.text.faint,
            marginTop: theme.spacing.sm,
          }}>
            Prices and PPG you set are league-wide — they apply to every plan in this league, and to the player list.
          </div>

          <GroupHeading count={starters.length} group={byGroup.starters} ppg={groupPpg.starters.ppg} unprojected={groupPpg.starters.unprojected}>STARTERS</GroupHeading>
          <div style={{ display: "flex", flexDirection: "column", gap: PLAN_GAP }}>
            {starters.map(slot => <SlotRow key={slot.id} {...rowProps(slot)} />)}
          </div>

          {bench.length > 0 && (
            <>
              <GroupHeading count={bench.length} group={byGroup.bench} ppg={groupPpg.bench.ppg} unprojected={groupPpg.bench.unprojected}>BENCH</GroupHeading>
              <div style={{ display: "flex", flexDirection: "column", gap: PLAN_GAP }}>
                {bench.map(slot => <SlotRow key={slot.id} {...rowProps(slot)} />)}
              </div>
            </>
          )}
        </div>

        {/* 🔴 KEYED ON slotId — this is the "no QBs" bug's real fix.
              PlayerPanel holds posFilter and search as local state, and a
              single-position slot renders no pills to clear them with. So a WR
              filter set on a FLEX or bench row survived into a QB / K / DEF / TE
              cell, where allowed={QB} AND posFilter="WR" is an empty list with
              no visible control to escape it — only a reload recovered.
              Remounting on slot change resets both.

              🔴 slotId and NOT cellIdx, deliberately. Moving across cells within
              one row KEEPS your search and filter, because you are filling that
              row's backups and re-typing the same search four times is the
            annoyance this would otherwise trade for. Changing rows resets. */}
        {/* 🔴 INLINE ON A DESKTOP ONLY. Below 640 the same panel, with the same
            props, is rendered inside PlayerSheet at the bottom of this file. */}
        {!phone && !playersHidden && (
          <PlayerPanel key={activeCell?.slotId ?? "none"} {...panelProps} onAdd={addFromPanel} />
        )}
      </div>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <div style={{
        display: "flex", gap: theme.spacing.md, flexWrap: "wrap",
        marginTop: theme.spacing.huge,
        paddingTop: theme.spacing.lg,
        borderTop: `1px solid ${theme.colors.border.subtle}`,
      }}>
        <ToolbarButton onClick={handleCopy}>{copied ? "Copied" : "Copy plan as text"}</ToolbarButton>
        <ToolbarButton onClick={handlePrint}>Print</ToolbarButton>
        <div style={{ flex: 1 }} />
        {/* Two-step rather than a confirm() — a native modal dialog blocks the
            page, and this is recoverable-looking enough to deserve a beat. */}
        <ToolbarButton
          danger
          active={confirmClear}
          onClick={() => {
            if (confirmClear) { clearPlan(); setConfirmClear(false); }
            else { setConfirmClear(true); setTimeout(() => setConfirmClear(false), 3000); }
          }}
        >{confirmClear ? "Clear every target?" : "Clear plan"}</ToolbarButton>
      </div>

      {/* 🔴 A WAY IN THAT IS NOT A CELL. Every other route into the list starts
          by tapping a roster cell, which makes browsing — pricing a player,
          checking a rank, starring somebody — something you can only do by
          pretending to fill a slot you may not want to fill. Fixed above the
          fold so it is reachable from anywhere down a sixteen-row plan, and it
          opens the sheet UNSCOPED: the panel with no slot is the whole pool,
          which is its documented default state rather than an empty one. */}
      {phone && (
        <button
          type="button"
          data-testid="phone-open-players"
          onClick={() => { setActiveCell(null); setSheetOpen(true); }}
          style={{
            position: "fixed",
            right: theme.spacing.lg,
            bottom: theme.spacing.lg,
            zIndex: 120,
            padding: `${theme.spacing.md}px ${theme.spacing.xl}px`,
            borderRadius: theme.radius.xxl,
            border: `1px solid ${theme.colors.brand.orange}`,
            background: theme.colors.brand.orange,
            color: "#fff",
            fontFamily: "inherit",
            fontSize: theme.fontSize.caption,
            fontWeight: theme.fontWeight.heavy,
            letterSpacing: "0.06em",
            boxShadow: "0 6px 18px rgba(0,0,0,0.45)",
            cursor: "pointer",
          }}
        >+ PLAYERS</button>
      )}
    </div>

    {/* Outside .dp-root so the fixed positioning cannot be trapped by a
        transform or an overflow on an ancestor. Phone only, and never mounted
        at all on a desktop — the inline panel above is the desktop's list. */}
    {phone && (
      <PlayerSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title={sheetTitle}
      >
        <PlayerPanel
          key={activeCell?.slotId ?? "none"}
          {...panelProps}
          autoFocusSearch
          onAdd={(player) => { addFromPanel(player); setSheetOpen(false); }}
        />
      </PlayerSheet>
    )}
    </>
  );
}
